import { Field, ID, ObjectType } from 'type-graphql'
import { Column, Entity, Index, ManyToOne, ManyToMany, PrimaryGeneratedColumn, RelationId } from 'typeorm'

import {
  HistoryActionColumn,
  HistoryActionType,
  HistoryEntityInterface,
  HistoryOriginalIdColumn
} from '@operato/typeorm-history'
import { User } from '@things-factory/auth-base'
import { Domain } from '@things-factory/shell'
import { config } from '@things-factory/env'

import { Board } from './board.js'
import { Group } from '../group/group.js'
import { PlayGroup } from '../play-group/play-group.js'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type

@Entity()
@Index('ix_board_history_0', (boardHistory: BoardHistory) => [boardHistory.originalId, boardHistory.version], {
  unique: true
})
@ObjectType({ description: 'Stores historical versions of board entities.' })
export class BoardHistory implements HistoryEntityInterface<Board> {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for the board history record.' })
  readonly id: string

  @Column({ nullable: true, default: 1 })
  @Field({ nullable: true, description: 'The version number of this board history record.' })
  version?: number = 1

  @ManyToOne(type => Domain)
  @Field(type => Domain, { nullable: true, description: 'The domain this board belonged to.' })
  domain?: Domain

  @RelationId((board: Board) => board.domain)
  domainId?: string

  @Column()
  @Field({ description: 'The name of the board at this point in history.' })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The description of the board at this point in history.' })
  description?: string

  @Column({ nullable: true, default: 'main' })
  @Field({
    nullable: true,
    description: "The type of the board at this point in history, can be 'main', 'sub', or 'popup'."
  })
  type?: 'main' | 'sub' | 'popup'

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'The JSON model of the board at this point in history.' })
  model?: string

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'The base64 encoded thumbnail image of the board at this point in history.' })
  thumbnail?: string

  @ManyToOne(type => Group, group => group.boards)
  @Field(type => Group, { nullable: true, description: 'The group the board belonged to at this point in history.' })
  group?: Group

  @RelationId((board: Board) => board.group)
  groupId?: string

  @ManyToMany(type => PlayGroup, playGroup => playGroup.boards)
  @Field(type => [PlayGroup], {
    nullable: true,
    description: 'The play groups the board was part of at this point in history.'
  })
  playGroups?: PlayGroup[]

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The creation timestamp of the original board.' })
  createdAt?: Date

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The last update timestamp of the board at this point in history.' })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'The user who created the original board.' })
  creator?: User

  @RelationId((board: Board) => board.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'The user who made the changes recorded in this history entry.' })
  updater?: User

  @RelationId((board: Board) => board.updater)
  updaterId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The deletion timestamp of the board, if applicable.' })
  deletedAt?: Date

  @HistoryOriginalIdColumn()
  @Field({ description: 'The ID of the original board entity this history record belongs to.' })
  public originalId!: string

  @HistoryActionColumn({
    nullable: false,
    type:
      DATABASE_TYPE == 'postgres' || DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'enum'
        : DATABASE_TYPE == 'oracle'
          ? 'varchar2'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    enum:
      DATABASE_TYPE == 'postgres' || DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? HistoryActionType
        : undefined,
    length: DATABASE_TYPE == 'postgres' || DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb' ? undefined : 10
  })
  @Field(type => String, {
    description: 'The action that created this history record (e.g., created, updated, deleted).'
  })
  public action!: HistoryActionType
}
